<template>
  <div class="flex flex-col gap-6">
    <!-- Header -->
    <div>
      <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100">
        {{ $t('AI_SETTINGS.SUMMARY.TITLE') }}
      </h3>
      <p class="text-sm text-slate-600 dark:text-slate-400">
        {{ $t('AI_SETTINGS.SUMMARY.DESCRIPTION') }}
      </p>
    </div>

    <!-- Enable/Disable Toggle -->
    <div class="rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-slate-900/30 backdrop-blur-md shadow-[0_1px_2px_rgba(0,0,0,0.06),0_8px_24px_rgba(0,0,0,0.06)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.35),0_8px_24px_rgba(0,0,0,0.4)] p-6">
      <div class="flex items-center justify-between">
        <div>
          <h4 class="text-base font-medium text-slate-900 dark:text-slate-100">
            {{ $t('AI_SETTINGS.SUMMARY.ENABLE_TOGGLE.TITLE') }}
          </h4>
          <p class="text-sm text-slate-600 dark:text-slate-400">
            {{ $t('AI_SETTINGS.SUMMARY.ENABLE_TOGGLE.DESCRIPTION') }}
          </p>
        </div>
        <div class="ml-4">
          <woot-switch
            :model-value="isSummaryEnabled"
            @input="toggleSummaryEnabled"
          />
        </div>
      </div>
    </div>

    <!-- Prompt Settings -->
    <div class="rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-slate-900/30 backdrop-blur-md shadow-[0_1px_2px_rgba(0,0,0,0.06),0_8px_24px_rgba(0,0,0,0.06)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.35),0_8px_24px_rgba(0,0,0,0.4)] p-6">
      <div class="mb-4">
        <h4 class="text-base font-medium text-slate-900 dark:text-slate-100">
          {{ $t('AI_SETTINGS.SUMMARY.PROMPT.TITLE') }}
        </h4>
        <p class="text-sm text-slate-600 dark:text-slate-400">
          {{ $t('AI_SETTINGS.SUMMARY.PROMPT.DESCRIPTION') }}
        </p>
      </div>

      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {{ $t('AI_SETTINGS.SUMMARY.PROMPT.LABEL') }}
          </label>
          <TextArea
            v-model="summaryPrompt"
            name="conversation-summary-prompt"
            :placeholder="$t('AI_SETTINGS.SUMMARY.PROMPT.PLACEHOLDER')"
            :message="''"
            :message-type="'info'"
            auto-height
            :resize="true"
            :min-height="'100px'"
            rows="6"
            :disabled="!isSummaryEnabled"
            @update:modelValue="onPromptChange"
          />
        </div>

        <div class="flex justify-end space-x-3">
          <woot-button
            color-scheme="secondary"
            class="input-base"
            :disabled="!isSummaryEnabled || !hasChanges"
            @click="resetToDefault"
          >
            {{ $t('AI_SETTINGS.SUMMARY.RESET_DEFAULT') }}
          </woot-button>
          <woot-button
            variant="smooth"
            color-scheme="success"
            class="input-base"
            :disabled="!isSummaryEnabled || !hasChanges || isLoading"
            :loading="isLoading"
            @click="saveSummarySettings"
          >
            {{ $t('AI_SETTINGS.SUMMARY.SAVE') }}
          </woot-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAlert } from 'dashboard/composables';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

export default {
  name: 'ConversationSummaryTab',
  components: { TextArea },
  
  data() {
    return {
      isSummaryEnabled: false,
      summaryPrompt: '',
      originalPrompt: '',
      hasChanges: false,
      isLoading: false,
      defaultPrompt: 'Tóm tắt cuộc hội thoại này một cách ngắn gọn và rõ ràng. Bao gồm các điểm chính, vấn đề quan trọng và kết quả (nếu có). Viết bằng tiếng Việt với giọng điệu chuyên nghiệp.',
    };
  },
  
  mounted() {
    this.loadSummarySettings();
  },
  
  methods: {
    getApiErrorMessage(error, fallbackKey) {
      return error?.response?.data?.error || this.$t(fallbackKey);
    },

    async loadSummarySettings() {
      try {
        this.isLoading = true;
        const accountId = this.$store.getters.getCurrentAccountId;
        
        // Load summary settings from API
        const response = await window.axios.get(`/api/v1/accounts/${accountId}/ai_summary_settings`);
        
        if (response.data) {
          this.isSummaryEnabled = response.data.enabled || false;
          this.summaryPrompt = response.data.prompt || this.defaultPrompt;
          this.originalPrompt = this.summaryPrompt;
        } else {
          // Set default values if no settings exist
          this.summaryPrompt = this.defaultPrompt;
          this.originalPrompt = this.summaryPrompt;
        }
      } catch (error) {
        console.error('Error loading summary settings:', error);
        // Set default values on error
        this.summaryPrompt = this.defaultPrompt;
        this.originalPrompt = this.summaryPrompt;
      } finally {
        this.isLoading = false;
      }
    },
    
    onPromptChange() {
      this.hasChanges = this.summaryPrompt !== this.originalPrompt;
    },
    
    resetToDefault() {
      this.summaryPrompt = this.defaultPrompt;
      this.hasChanges = this.summaryPrompt !== this.originalPrompt;
    },
    
    async toggleSummaryEnabled(newValue) {
      console.log('toggleSummaryEnabled called', newValue);
      this.isSummaryEnabled = newValue;
      
      try {
        const accountId = this.$store.getters.getCurrentAccountId;
        console.log('Sending API request with accountId:', accountId);
        
        const response = await window.axios.put(`/api/v1/accounts/${accountId}/ai_summary_settings`, {
          enabled: this.isSummaryEnabled,
          prompt: this.summaryPrompt
        });
        
        console.log('API response:', response);
        const message = this.isSummaryEnabled 
          ? this.$t('AI_SETTINGS.SUMMARY.ENABLED_SUCCESS')
          : this.$t('AI_SETTINGS.SUMMARY.DISABLED_SUCCESS');
        useAlert(message, { type: 'success' });
      } catch (error) {
        console.error('API error:', error);
        // Revert toggle state on error
        this.isSummaryEnabled = !this.isSummaryEnabled;
        const errorMessage = this.getApiErrorMessage(
          error,
          'AI_SETTINGS.SUMMARY.UPDATE_ERROR'
        );
        useAlert(errorMessage, {
          type: 'error',
        });
      }
    },
    
    async saveSummarySettings() {
      try {
        this.isLoading = true;
        const accountId = this.$store.getters.getCurrentAccountId;
        
        await window.axios.put(`/api/v1/accounts/${accountId}/ai_summary_settings`, {
          enabled: this.isSummaryEnabled,
          prompt: this.summaryPrompt
        });
        
        this.originalPrompt = this.summaryPrompt;
        this.hasChanges = false;
        useAlert(this.$t('AI_SETTINGS.SUMMARY.SAVE_SUCCESS'), {
          type: 'success',
        });
      } catch (error) {
        const errorMessage = this.getApiErrorMessage(
          error,
          'AI_SETTINGS.SUMMARY.SAVE_ERROR'
        );
        useAlert(errorMessage, {
          type: 'error',
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script> 
